import { Suspense, lazy, useEffect } from "react";
import { Route, Routes } from "react-router-dom";

import { ProtectedRoute } from "@/components/ProtectedRoute";
import { RootErrorBoundary, RouteErrorBoundary } from "@/components/error-boundary";
import { PageSkeleton } from "@/components/page-skeleton";
import { SupportSessionBanner } from "@/components/support-session-banner";
import { Toaster } from "@/components/ui/toast";
import { TooltipProvider } from "@/components/ui/tooltip";
import HomePage from "@/pages/HomePage";
import ForgotPasswordPage from "@/pages/auth/ForgotPasswordPage";
import LoginPage from "@/pages/auth/LoginPage";
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage";
import SignUpPage from "@/pages/auth/SignUpPage";
import VerifyEmailPage from "@/pages/auth/VerifyEmailPage";
import PrivacyPage from "@/pages/legal/PrivacyPage";
import TermsPage from "@/pages/legal/TermsPage";
import { useAuthStore } from "@/stores/auth";

// Lazy-load every protected page — keeps the initial bundle small. Public
// pages (Home/Login/SignUp) ship with the entry chunk so first paint is fast.
const OnboardingPage = lazy(() => import("@/pages/onboarding/OnboardingPage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const ImportsHubPage = lazy(() => import("@/pages/ImportsHubPage"));
const EmployeeListPage = lazy(() => import("@/pages/employees/EmployeeListPage"));
const EmployeeFormPage = lazy(() => import("@/pages/employees/EmployeeFormPage"));
const EmployeeDetailPage = lazy(() => import("@/pages/employees/EmployeeDetailPage"));
const EmployeeImportPage = lazy(() => import("@/pages/employees/EmployeeImportPage"));
const TimeEntriesPage = lazy(() => import("@/pages/timekeeping/TimeEntriesPage"));
const TimeEntryImportPage = lazy(() => import("@/pages/timekeeping/TimeEntryImportPage"));
const HistoricalDtrImportPage = lazy(
  () => import("@/pages/timekeeping/HistoricalDtrImportPage"),
);
const LoanImportPage = lazy(() => import("@/pages/compensation/LoanImportPage"));
const AllowanceImportPage = lazy(
  () => import("@/pages/compensation/AllowanceImportPage"),
);
const CustomComponentImportPage = lazy(
  () => import("@/pages/compensation/CustomComponentImportPage"),
);
const LeaveBalanceImportPage = lazy(
  () => import("@/pages/leaves/LeaveBalanceImportPage"),
);
const YtdSeedImportPage = lazy(() => import("@/pages/payroll/YtdSeedImportPage"));
const SelfClockPage = lazy(() => import("@/pages/clock/SelfClockPage"));
const KioskPage = lazy(() => import("@/pages/kiosk/KioskPage"));
const DTRListPage = lazy(() => import("@/pages/dtr/DTRListPage"));
const LeavesPage = lazy(() => import("@/pages/leaves/LeavesPage"));
const OTPage = lazy(() => import("@/pages/ot/OTPage"));
const HolidaysPage = lazy(() => import("@/pages/holidays/HolidaysPage"));
const PayrollListPage = lazy(() => import("@/pages/payroll/PayrollListPage"));
const PayrollRunPage = lazy(() => import("@/pages/payroll/PayrollRunPage"));
const PayslipDetailPage = lazy(() => import("@/pages/payroll/PayslipDetailPage"));
const FilingsPage = lazy(() => import("@/pages/filings/FilingsPage"));
const BooksPage = lazy(() => import("@/pages/books/BooksPage"));
const JournalPage = lazy(() => import("@/pages/books/JournalPage"));
const SalesPage = lazy(() => import("@/pages/books/SalesPage"));
const PurchasesPage = lazy(() => import("@/pages/books/PurchasesPage"));
const TaxPage = lazy(() => import("@/pages/books/TaxPage"));
const BankPage = lazy(() => import("@/pages/books/BankPage"));
const ProductListPage = lazy(() => import("@/pages/inventory/ProductListPage"));
const POSPage = lazy(() => import("@/pages/inventory/POSPage"));
const InventoryReportsPage = lazy(() => import("@/pages/inventory/ReportsPage"));
const InventoryPurchasesPage = lazy(() => import("@/pages/inventory/PurchasesPage"));
const InventorySalesPage = lazy(() => import("@/pages/inventory/SalesHistoryPage"));
const InventorySuppliersPage = lazy(() => import("@/pages/inventory/SuppliersPage"));
const OrgSettingsPage = lazy(() => import("@/pages/settings/OrgSettingsPage"));
const LeaveTypesPage = lazy(() => import("@/pages/settings/LeaveTypesPage"));
const ESSDashboardPage = lazy(() => import("@/pages/ess/ESSDashboardPage"));
const ESSPayslipsPage = lazy(() => import("@/pages/ess/ESSPayslipsPage"));
const ESSPayslipDetailPage = lazy(() => import("@/pages/ess/ESSPayslipDetailPage"));
const ESSDTRsPage = lazy(() => import("@/pages/ess/ESSDTRsPage"));
const ESSProfilePage = lazy(() => import("@/pages/ess/ESSProfilePage"));
const TenantsListPage = lazy(() => import("@/pages/superadmin/TenantsListPage"));
const AuditLogPage = lazy(() => import("@/pages/superadmin/AuditLogPage"));

export default function App() {
  const hydrate = useAuthStore((s) => s.hydrate);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  return (
    <RootErrorBoundary>
      <TooltipProvider delayDuration={300}>
        <SupportSessionBanner />
        <RouteErrorBoundary>
          <Suspense fallback={<PageSkeleton />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/signup" element={<SignUpPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/onboarding" element={<Protected element={<OnboardingPage />} />} />
              <Route path="/dashboard" element={<Protected element={<DashboardPage />} />} />
              <Route path="/imports" element={<Protected element={<ImportsHubPage />} roles={HR_ROLES} />} />
              <Route path="/employees" element={<Protected element={<EmployeeListPage />} />} />
              <Route path="/employees/new" element={<Protected element={<EmployeeFormPage />} />} />
              <Route path="/employees/import" element={<Protected element={<EmployeeImportPage />} roles={HR_ROLES} />} />
              <Route path="/employees/:id" element={<Protected element={<EmployeeDetailPage />} />} />
              <Route path="/employees/:id/edit" element={<Protected element={<EmployeeFormPage />} />} />
              <Route path="/time-entries" element={<Protected element={<TimeEntriesPage />} />} />
              <Route path="/time-entries/import" element={<Protected element={<TimeEntryImportPage />} roles={HR_ROLES} />} />
              <Route path="/dtrs/import" element={<Protected element={<HistoricalDtrImportPage />} roles={HR_ROLES} />} />
              <Route path="/compensation/loans/import" element={<Protected element={<LoanImportPage />} roles={HR_ROLES} />} />
              <Route path="/compensation/allowances/import" element={<Protected element={<AllowanceImportPage />} roles={HR_ROLES} />} />
              <Route path="/compensation/custom-components/import" element={<Protected element={<CustomComponentImportPage />} roles={HR_ROLES} />} />
              <Route path="/leaves/balances/import" element={<Protected element={<LeaveBalanceImportPage />} roles={HR_ROLES} />} />
              <Route path="/payroll/ytd-seed/import" element={<Protected element={<YtdSeedImportPage />} roles={HR_ROLES} />} />
              <Route path="/clock" element={<Protected element={<SelfClockPage />} />} />
              <Route path="/kiosk" element={<Protected element={<KioskPage />} />} />
              <Route path="/dtrs" element={<Protected element={<DTRListPage />} />} />
              <Route path="/leaves" element={<Protected element={<LeavesPage />} />} />
              <Route path="/ot" element={<Protected element={<OTPage />} />} />
              <Route path="/holidays" element={<Protected element={<HolidaysPage />} />} />
              <Route path="/payroll" element={<Protected element={<PayrollListPage />} />} />
              <Route path="/payroll/:id" element={<Protected element={<PayrollRunPage />} />} />
              <Route path="/payslips/:id" element={<Protected element={<PayslipDetailPage />} />} />
              <Route path="/filings" element={<Protected element={<FilingsPage />} />} />
              <Route path="/books" element={<Protected element={<BooksPage />} roles={BOOKS_ROLES} />} />
              <Route path="/books/journal" element={<Protected element={<JournalPage />} roles={HR_ROLES} />} />
              <Route path="/books/sales" element={<Protected element={<SalesPage />} roles={HR_ROLES} />} />
              <Route path="/books/purchases" element={<Protected element={<PurchasesPage />} roles={HR_ROLES} />} />
              <Route path="/books/tax" element={<Protected element={<TaxPage />} roles={BOOKS_ROLES} />} />
              <Route path="/books/bank" element={<Protected element={<BankPage />} roles={HR_ROLES} />} />
              <Route path="/inventory" element={<Protected element={<ProductListPage />} roles={INVENTORY_ROLES} />} />
              <Route path="/inventory/pos" element={<Protected element={<POSPage />} roles={INVENTORY_ROLES} />} />
              <Route path="/inventory/reports" element={<Protected element={<InventoryReportsPage />} roles={INVENTORY_ROLES} />} />
              <Route path="/inventory/purchases" element={<Protected element={<InventoryPurchasesPage />} roles={INVENTORY_ROLES} />} />
              <Route path="/inventory/sales" element={<Protected element={<InventorySalesPage />} roles={INVENTORY_ROLES} />} />
              <Route path="/inventory/suppliers" element={<Protected element={<InventorySuppliersPage />} roles={INVENTORY_ROLES} />} />
              <Route path="/settings/org" element={<Protected element={<OrgSettingsPage />} />} />
              <Route path="/settings/leave-types" element={<Protected element={<LeaveTypesPage />} />} />
              <Route path="/ess" element={<Protected element={<ESSDashboardPage />} />} />
              <Route path="/ess/payslips" element={<Protected element={<ESSPayslipsPage />} />} />
              <Route path="/ess/payslips/:id" element={<Protected element={<ESSPayslipDetailPage />} />} />
              <Route path="/ess/dtrs" element={<Protected element={<ESSDTRsPage />} />} />
              <Route path="/ess/profile" element={<Protected element={<ESSProfilePage />} />} />
              <Route path="/superadmin/tenants" element={<Protected element={<TenantsListPage />} />} />
              <Route path="/superadmin/audit" element={<Protected element={<AuditLogPage />} />} />
              <Route path="*" element={<HomePage />} />
            </Routes>
          </Suspense>
        </RouteErrorBoundary>
        <Toaster />
      </TooltipProvider>
    </RootErrorBoundary>
  );
}

function Protected({
  element,
  roles,
}: {
  element: React.ReactElement;
  roles?: import("@/types/api").Role[];
}) {
  return <ProtectedRoute roles={roles}>{element}</ProtectedRoute>;
}

// Bulk-import routes are HR-tier only. The backend gates the underlying
// endpoints to IsHRAdmin already, but route-level gating gives clearer
// UX than "page renders, every API call 403s".
const HR_ROLES: import("@/types/api").Role[] = ["OWNER", "HR_ADMIN"];

// KraetdBooks is HR-tier + auditor (read-only). The page itself also checks
// the per-tenant `bookkeeping_enabled` flag and shows a disabled state.
const BOOKS_ROLES: import("@/types/api").Role[] = ["OWNER", "HR_ADMIN", "AUDITOR"];

// KraetdStock (inventory) — HR-tier + auditor (read-only). Pages also check
// the per-tenant `inventory_enabled` flag and show a disabled state.
const INVENTORY_ROLES: import("@/types/api").Role[] = ["OWNER", "HR_ADMIN", "MANAGER", "AUDITOR"];
